/**
 * The centralised sign-in, stated once — the rules the D1 directory and the app
 * both work from.
 *
 * The unit signs in with a phone number and one shared group password. The
 * directory in the cloud says which numbers are allowed and at what level; this
 * module is the translation between the two roles that directory speaks and the
 * three account roles the local permissions engine enforces.
 *
 * | D1 `users.role` | level | local `User.role` | what it means |
 * | --- | --- | --- | --- |
 * | `admin` | מנהל על | `super_admin` | everything, plus the directory and the system settings |
 * | `manager` | מפקד / בעל תפקיד | `admin` | tasks, soldiers and vehicles in full — and nothing of the directory |
 *
 * The `driver` level (`soldier`) has no cloud role on purpose: a נהג's account
 * is opened on the roster by the מנהל על and stays local, exactly as it was
 * before the directory existed. The mandate named two roles, and these are they.
 *
 * Nothing here touches the network, Dexie or `localStorage` — `src/db/
 * cloud-auth-store.ts` does all three, and the healthcheck executes the
 * decisions in this module directly.
 */
import { storageKey } from './config';
import { syncEndpoint } from './sync-engine';
import type { UserRole } from '../types/user';

/** The roles the D1 directory holds. */
export const CLOUD_ROLES = ['admin', 'manager'] as const;

export type CloudRole = (typeof CLOUD_ROLES)[number];

export const CLOUD_ROLE_LABELS: Record<CloudRole, string> = {
  admin: 'מנהל על',
  manager: 'מפקד / בעל תפקיד',
};

/** The sentence the "ניהול משתמשים" screen puts under each role. */
export const CLOUD_ROLE_DESCRIPTIONS: Record<CloudRole, string> = {
  admin: 'גישה מלאה לכל המסכים, לניהול המשתמשים ולהגדרות המערכת.',
  manager:
    'ניהול מלא של משימות, חיילים ורכבים. ללא גישה לניהול המשתמשים ולהגדרות המערכת.',
};

/** The shared code the whole group signs in with — four digits. */
export const GROUP_PASSWORD_LENGTH = 4;

export const isValidGroupPassword = (raw: string | null | undefined): boolean =>
  new RegExp(`^\\d{${GROUP_PASSWORD_LENGTH}}$`).test(String(raw ?? ''));

/** Where the signed session token is kept between launches. */
export const AUTH_TOKEN_KEY = storageKey('auth-token');

/** The account role a cloud role is enforced as. */
export const CLOUD_ROLE_ACCOUNT_ROLES: Record<CloudRole, UserRole> = {
  admin: 'super_admin',
  manager: 'admin',
};

export const isCloudRole = (raw: unknown): raw is CloudRole =>
  CLOUD_ROLES.includes(String(raw ?? '') as CloudRole);

/**
 * The local role a directory row is enforced at. An unrecognised role is read
 * as `manager` — the narrower of the two, because a role nobody recognises must
 * never be the one that opens the directory.
 */
export const accountRoleForCloudRole = (role: string | null | undefined): UserRole =>
  CLOUD_ROLE_ACCOUNT_ROLES[isCloudRole(role) ? role : 'manager'];

/**
 * The reverse reading — the directory role an account would be filed under.
 * `soldier` has no cloud role of its own, so a driver reads as `manager`: it is
 * the level the directory would hold them at if they were ever added to it.
 */
export const cloudRoleForAccountRole = (role: UserRole): CloudRole =>
  role === 'super_admin' ? 'admin' : 'manager';

/** How a directory row arrives from `/api/login`, `/api/me` and `/api/users`. */
export interface CloudUser {
  id: string;
  phone: string;
  name: string;
  role: CloudRole;
  /** Only the directory listing carries these; a login reply does not. */
  isActive?: boolean;
  createdAt?: string | null;
  updatedAt?: string | null;
}

/** What a completed sign-in hands back. */
export interface CloudLoginResult {
  token: string;
  user: CloudUser;
}

/** Why a sign-in did not complete. */
export type CloudAuthFailure =
  /** The server answered, and said no. The message is its own. */
  | { kind: 'rejected'; status: number; message: string }
  /** No answer at all — no signal, no backend, a dev server with no functions. */
  | { kind: 'unreachable'; message: string };

/** The message a refusal with no body of its own is reported as. */
export const CLOUD_AUTH_MESSAGES = {
  rejected: 'הכניסה נדחתה — בדקו את המספר ואת סיסמת הקבוצה',
  unreachable: 'אין חיבור לשרת — נכנסים במצב מקומי',
  disabled: 'החשבון אינו פעיל — פנו למנהל',
  forbidden: 'הפעולה שמורה למנהל המערכת בלבד',
} as const;

/**
 * Where an auth route lives. Same rule as the mirror: an explicit
 * `VITE_API_BASE_URL` wins, and with none set the app talks to `/api` on the
 * origin it was served from.
 */
export const authEndpoint = (baseUrl: string | null | undefined, path: string): string =>
  syncEndpoint(baseUrl, path);

/**
 * Whether a refusal means "this session is over" rather than "that request was
 * not allowed".
 *
 * 401 is a token that is missing, forged or expired. 403 is an account the
 * directory has since disabled or removed. Both end the session — a 403 on a
 * route the account was never entitled to (a manager reaching for `/api/users`)
 * never gets this far, because the screen does not offer it.
 */
export const isSessionEndingStatus = (status: number): boolean =>
  status === 401 || status === 403;
